import React, { useState, useRef } from 'react'
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import ReactSelect, { components } from "react-select";
import Modal from 'react-modal';
import styles from './styles.module.scss'
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import ButtonOutlined from "../../../../components/FormElements/ButtonOutlined";
import Checkbox from "../../../../components/FormElements/Checkbox";
import { Badge } from "../../../../components/Badge";
import { store } from "react-notifications-component";
import branchApi from "../../../../store/branchStore/branchApi";
import instance from "../../../../api";

const CAMPAIGN_TYPES = {
  ALL_USERS: "ALL_USERS",
  EXCEL_FILE: "EXCEL_FILE",
  RFM_CATEGORY: "RFM_CATEGORY",
  PHONE_SEARCH: "PHONE_SEARCH"
}

const RFM_CATEGORIES = [
  { value: 'TOP_CUSTOMERS', label: 'Самые главные' },
  { value: 'ACTIVE_CUSTOMERS', label: 'Активные' },
  { value: 'EMERGING_CUSTOMERS', label: 'Перспективные' },
  { value: 'UNSTEADY_CUSTOMERS', label: 'Непостоянные' },
  { value: 'POTENTIAL_LOST_CUSTOMERS', label: 'В зоне риска' },
  { value: 'INACTIVE_CUSTOMERS', label: 'Неактивные' },
  { value: 'LOST_CUSTOMERS', label: 'Потерянные' }
]

const INITIAL_VALUES = {
  campaign_type: CAMPAIGN_TYPES.ALL_USERS,
  rfm_category: '',
  file: null,
  selected_phones: []
}

const errorModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '500px',
    width: '90%',
    borderRadius: '12px',
    padding: '24px',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const ERROR_MESSAGES = {
  'expire_interval must be at least 1 day': 'Срок действия реферальной ссылки должен быть не менее 1 дня. Проверьте настройки реферальной программы.',
  'award type is not configured': 'Тип награды не настроен. Укажите тип награды (фиксированная сумма или процент) в настройках реферальной программы.',
  'referrer or referee percentage must be set': 'Процент награды для реферера или реферала должен быть указан. Проверьте настройки наград.',
  'referrer or referee fixed amount must be set': 'Фиксированная сумма награды для реферера или реферала должна быть указана. Проверьте настройки наград.',
  'poll question is not configured': 'Вопрос для опроса не настроен. Заполните вопрос опроса в настройках реферальной программы.',
  'poll yes/no responses are not configured': 'Варианты ответа "Да/Нет" для опроса не настроены. Заполните варианты ответов в настройках реферальной программы.',
  'referral config is nil': 'Реферальная программа не настроена для этого филиала. Сначала настройте реферальную программу.',
};

function getReadableError(message) {
  if (!message) return 'Произошла неизвестная ошибка';
  for (const [key, value] of Object.entries(ERROR_MESSAGES)) {
    if (message.includes(key)) return value;
  }
  if (message.includes('referral config is invalid')) {
    return 'Конфигурация реферальной программы некорректна. Проверьте все обязательные поля в настройках.';
  }
  return message;
}

const ReferralSystem = () => {
  const { t } = useTranslation()
  const { info } = useSelector((state) => state.branchReducer)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorModal, setErrorModal] = useState(null)
  const fileInputRef = useRef(null)

  // Phone search state
  const [phoneOptions, setPhoneOptions] = useState([])
  const [phoneInputValue, setPhoneInputValue] = useState("")
  const [phoneLoading, setPhoneLoading] = useState(false)

  const loadPhoneOptions = async (inputValue) => {
    if (phoneLoading || !inputValue || inputValue.length !== 4) return;

    setPhoneLoading(true);

    try {
      const response = await instance().get("/user/phone/search", {
        params: {
          query: inputValue,
          pageNumber: 0,
          pageSize: 50,
        },
      });

      const newOptions = response.data.data.map((option) => ({
        label: option.fullName?.replace(/undefined/g, "") || "",
        value: option.phone
      }));
      setPhoneOptions(newOptions);
    } catch (error) {
      console.error("Error loading phone options", error);
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneInputChange = (newValue) => {
    setPhoneInputValue(newValue);
    if (newValue.length === 4) {
      loadPhoneOptions(newValue);
    } else {
      setPhoneOptions([]);
    }
  };

  const CustomPhoneOption = (props) => {
    return (
      <components.Option {...props}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 500 }}>{props.data.value}</span>
          {props.data.label && <span style={{ color: '#77797B', fontSize: 14 }}>{props.data.label}</span>}
        </div>
      </components.Option>
    );
  };

  const XLSIcon = (
    <svg style={{ flexShrink: 0 }} width="30" height="30" viewBox="0 0 30 30" fill="none">
      <path d="M18.2888 24.375C18.2888 24.6236 18.19 24.8621 18.0142 25.0379C17.8384 25.2137 17.5999 25.3125 17.3513 25.3125H14.07C13.8214 25.3125 13.583 25.2137 13.4071 25.0379C13.2313 24.8621 13.1325 24.6236 13.1325 24.375V17.8125C13.1325 17.5639 13.2313 17.3254 13.4071 17.1496C13.583 16.9738 13.8214 16.875 14.07 16.875C14.3187 16.875 14.5571 16.9738 14.733 17.1496C14.9088 17.3254 15.0075 17.5639 15.0075 17.8125V23.4375H17.3513C17.5999 23.4375 17.8384 23.5363 18.0142 23.7121C18.19 23.8879 18.2888 24.1264 18.2888 24.375ZM10.865 17.0496C10.7648 16.978 10.6515 16.9269 10.5315 16.8991C10.4116 16.8714 10.2874 16.8675 10.1659 16.8877C10.0445 16.908 9.92821 16.9519 9.82376 17.0171C9.71931 17.0823 9.62872 17.1674 9.55716 17.2676L7.9763 19.4813L6.39544 17.2676C6.32643 17.1614 6.23662 17.0703 6.13144 16.9998C6.02625 16.9293 5.90787 16.8808 5.78343 16.8573C5.659 16.8338 5.53109 16.8357 5.40742 16.863C5.28376 16.8903 5.1669 16.9423 5.06391 17.016C4.96091 17.0897 4.87391 17.1835 4.80816 17.2917C4.7424 17.3999 4.69926 17.5203 4.68132 17.6457C4.66339 17.7711 4.67104 17.8988 4.70381 18.0211C4.73658 18.1434 4.7938 18.2578 4.872 18.3574L6.82435 21.0938L4.86966 23.8301C4.73811 24.0325 4.68971 24.2778 4.73452 24.515C4.77934 24.7522 4.91393 24.963 5.11026 25.1034C5.30659 25.2439 5.54953 25.3032 5.78849 25.269C6.02745 25.2348 6.24402 25.1098 6.3931 24.9199L7.9763 22.7062L9.55716 24.9199C9.70623 25.1098 9.9228 25.2348 10.1618 25.269C10.4007 25.3032 10.6437 25.2439 10.84 25.1034C11.0363 24.963 11.1709 24.7522 11.2157 24.515C11.2606 24.2778 11.2121 24.0325 11.0806 23.8301L9.12825 21.0938L11.0829 18.3574C11.1545 18.2572 11.2056 18.1439 11.2334 18.024C11.2612 17.9041 11.2651 17.7798 11.2448 17.6584C11.2246 17.5369 11.1806 17.4207 11.1154 17.3162C11.0503 17.2118 10.9652 17.1212 10.865 17.0496ZM22.4595 20.0754C21.9908 19.9395 21.5056 19.8 21.2349 19.6254C21.0884 19.5293 21.0908 19.5082 21.1036 19.4027C21.1054 19.3175 21.1276 19.234 21.1684 19.1591C21.2092 19.0843 21.2674 19.0203 21.338 18.9727C21.8771 18.607 23.1357 18.7711 23.6607 18.907C23.9012 18.9703 24.1571 18.9354 24.3719 18.81C24.5867 18.6846 24.7429 18.479 24.8062 18.2385C24.8694 17.9979 24.8345 17.7421 24.7091 17.5273C24.5838 17.3124 24.3782 17.1562 24.1376 17.093C23.8904 17.0285 21.6767 16.4812 20.2904 17.4164C19.998 17.6139 19.7526 17.8732 19.5714 18.1759C19.3902 18.4786 19.2776 18.8174 19.2415 19.1684C19.0072 21.0293 20.8411 21.5602 21.9368 21.8766C23.3501 22.2855 23.4743 22.4531 23.4345 22.766C23.3982 23.0484 23.2868 23.1562 23.1825 23.2266C22.6435 23.5852 21.406 23.4082 20.8927 23.2676C20.6526 23.2009 20.3959 23.2324 20.179 23.355C20.0716 23.4157 19.9772 23.497 19.9012 23.5942C19.8252 23.6914 19.7691 23.8026 19.7361 23.9215C19.7031 24.0404 19.6938 24.1646 19.7088 24.2871C19.7238 24.4095 19.7627 24.5278 19.8235 24.6352C19.9461 24.8521 20.1499 25.0115 20.39 25.0781C20.971 25.2312 21.5692 25.31 22.17 25.3125C22.8521 25.3125 23.6115 25.1953 24.2197 24.7898C24.5197 24.5903 24.7718 24.3268 24.9578 24.0182C25.1438 23.7096 25.2591 23.3636 25.2954 23.0051C25.5544 20.9766 23.6185 20.4105 22.4595 20.0754ZM4.69505 13.125V4.6875C4.69505 4.19022 4.89259 3.71331 5.24422 3.36167C5.59585 3.01004 6.07277 2.8125 6.57005 2.8125H17.82C17.9432 2.8124 18.0652 2.83657 18.179 2.88362C18.2928 2.93067 18.3962 2.99969 18.4833 3.08672L25.0458 9.64922C25.1329 9.73635 25.2019 9.83977 25.2489 9.95358C25.296 10.0674 25.3201 10.1893 25.32 10.3125V13.125C25.32 13.3736 25.2213 13.6121 25.0455 13.7879C24.8696 13.9637 24.6312 14.0625 24.3825 14.0625C24.1339 14.0625 23.8955 13.9637 23.7196 13.7879C23.5438 13.6121 23.445 13.3736 23.445 13.125V11.25H17.82C17.5714 11.25 17.333 11.1512 17.1571 10.9754C16.9813 10.7996 16.8825 10.5611 16.8825 10.3125V4.6875H6.57005V13.125C6.57005 13.3736 6.47128 13.6121 6.29546 13.7879C6.11965 13.9637 5.88119 14.0625 5.63255 14.0625C5.38391 14.0625 5.14545 13.9637 4.96964 13.7879C4.79382 13.6121 4.69505 13.3736 4.69505 13.125ZM18.7575 9.375H22.1185L18.7575 6.01289V9.375Z" fill="#6C6D6C"/>
    </svg>
  )

  const formik = useFormik({
    initialValues: { ...INITIAL_VALUES },
    onSubmit: async (values) => {
      if (!info?.id) return;

      setIsSubmitting(true);
      
      try {
        const formData = new FormData();
        formData.append('campaign_type', values.campaign_type);
        
        if (values.campaign_type === CAMPAIGN_TYPES.RFM_CATEGORY && values.rfm_category) {
          formData.append('rfm_category', values.rfm_category);
        }
        
        if (values.campaign_type === CAMPAIGN_TYPES.EXCEL_FILE && values.file) {
          formData.append('file', values.file);
        }

        if (values.campaign_type === CAMPAIGN_TYPES.PHONE_SEARCH && values.selected_phones.length > 0) {
          formData.append('phones', JSON.stringify(values.selected_phones.map(p => p.value)));
        }

        await branchApi.postBranchReferralCampaign(info.id, formData);

        store.addNotification({
          title: t('success'),
          message: t('referral.campaignStarted') || 'Реферальная кампания успешно запущена',
          type: "success",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });

        // Reset form
        formik.resetForm();
        setPhoneOptions([]);
        setPhoneInputValue("");
        
      } catch (error) {
        console.error('Referral campaign error:', error);
        const errorMessage = error.response?.data?.message || '';
        if (errorMessage.includes('referral config is invalid') || errorMessage.includes('referral config is nil')) {
          setErrorModal(getReadableError(errorMessage));
        } else {
          setErrorModal(getReadableError(errorMessage) || t('referral.campaignError') || 'Ошибка при запуске реферальной кампании');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue('file', file);
    }
  };

  const removeFile = () => {
    formik.setFieldValue('file', null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isFormValid = () => {
    if (formik.values.campaign_type === CAMPAIGN_TYPES.ALL_USERS) {
      return true;
    }
    if (formik.values.campaign_type === CAMPAIGN_TYPES.RFM_CATEGORY) {
      return formik.values.rfm_category !== '';
    }
    if (formik.values.campaign_type === CAMPAIGN_TYPES.EXCEL_FILE) {
      return formik.values.file !== null;
    }
    if (formik.values.campaign_type === CAMPAIGN_TYPES.PHONE_SEARCH) {
      return formik.values.selected_phones.length > 0;
    }
    return false;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <form onSubmit={formik.handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        
        <div style={{ display: "flex", flexDirection: 'column', gap: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>
            {t('referral.selectTarget') || 'Выберите целевую аудиторию:'}
          </h3>
          
          <Checkbox
            onChange={() => formik.setFieldValue('campaign_type', CAMPAIGN_TYPES.ALL_USERS)}
            checked={formik.values.campaign_type === CAMPAIGN_TYPES.ALL_USERS}
            label={t('referral.allUsers') || 'Все пользователи'}
          />
          
          <Checkbox
            onChange={() => formik.setFieldValue('campaign_type', CAMPAIGN_TYPES.EXCEL_FILE)}
            checked={formik.values.campaign_type === CAMPAIGN_TYPES.EXCEL_FILE}
            label={t('referral.excelFile') || 'Загрузить Excel файл с номерами'}
          />
          
          <Checkbox
            onChange={() => formik.setFieldValue('campaign_type', CAMPAIGN_TYPES.RFM_CATEGORY)}
            checked={formik.values.campaign_type === CAMPAIGN_TYPES.RFM_CATEGORY}
            label={t('referral.rfmCategory') || 'RFM категория'}
          />

          <Checkbox
            onChange={() => formik.setFieldValue('campaign_type', CAMPAIGN_TYPES.PHONE_SEARCH)}
            checked={formik.values.campaign_type === CAMPAIGN_TYPES.PHONE_SEARCH}
            label="Поиск по номеру телефона"
          />
        </div>

        {formik.values.campaign_type === CAMPAIGN_TYPES.EXCEL_FILE && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <ButtonOutlined onClick={handleFileSelect} variant="green" style={{ width: 192 }}>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  hidden
                />
                {t('referral.uploadFile') || 'Загрузить файл'}
              </ButtonOutlined>
              
              {formik.values.file && (
                <Badge style={{ flexShrink: 1 }} onDelete={removeFile}>
                  {XLSIcon}
                  <span style={{ color: '#6C6D6C', fontSize: 18, overflowWrap: 'anywhere' }}>
                    {formik.values.file.name}
                  </span>
                </Badge>
              )}
            </div>
            
            <p style={{ margin: 0, fontSize: 14, color: '#77797B' }}>
              {t('referral.fileRequirement') || 'Файл должен содержать колонку с номерами телефонов'}
            </p>
          </div>
        )}

        {formik.values.campaign_type === CAMPAIGN_TYPES.RFM_CATEGORY && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
              {t('referral.selectRFMCategory') || 'Выберите RFM категорию:'}
            </p>

            <select
              value={formik.values.rfm_category}
              onChange={(e) => formik.setFieldValue('rfm_category', e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '2px solid #E7E7E6',
                backgroundColor: 'white',
                fontSize: '16px',
                fontFamily: 'inherit'
              }}
              required
            >
              <option value="">{t('referral.chooseCategory') || 'Выберите категорию...'}</option>
              {RFM_CATEGORIES.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {formik.values.campaign_type === CAMPAIGN_TYPES.PHONE_SEARCH && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 500 }}>
              Найдите и выберите клиентов:
            </p>

            <ReactSelect
              isMulti
              value={formik.values.selected_phones}
              inputValue={phoneInputValue}
              placeholder="Введите последние 4 цифры номера (например: 7890)"
              onInputChange={handlePhoneInputChange}
              onChange={(selected) => formik.setFieldValue('selected_phones', selected || [])}
              options={phoneOptions}
              isLoading={phoneLoading}
              components={{ Option: CustomPhoneOption }}
              noOptionsMessage={() => phoneInputValue.length < 4
                ? 'Введите 4 цифры для поиска'
                : 'Клиенты не найдены'
              }
              loadingMessage={() => 'Поиск...'}
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: '6px',
                  border: '2px solid #E7E7E6',
                  minHeight: '42px',
                  fontSize: '16px'
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#E8F5E9',
                  borderRadius: '4px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#2E7D32',
                  fontWeight: 500
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#2E7D32',
                  ':hover': {
                    backgroundColor: '#C8E6C9',
                    color: '#1B5E20'
                  }
                })
              }}
            />

            <p style={{ margin: 0, fontSize: 14, color: '#77797B' }}>
              Для поиска клиента введите последние 4 цифры его номера телефона. Можно выбрать несколько клиентов.
            </p>
          </div>
        )}

        <div style={{ 
          backgroundColor: '#E3F2FD', 
          border: '1px solid #90CAF9',
          borderRadius: '6px',
          padding: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          fontSize: '14px',
          color: '#1565C0'
        }}>
          <span style={{ fontSize: '20px', marginTop: '2px' }}>ℹ️</span>
          <div>
            <strong>{t('referral.info') || 'Информация:'}</strong>
            <p style={{ margin: '8px 0 0 0' }}>
              {t('referral.campaignDescription') || 'Реферальная кампания отправит приглашения выбранной аудитории с возможностью получения бонусов за приведенных друзей.'}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16 }}>
          <ButtonFilled 
            variant="green" 
            type="submit" 
            loading={isSubmitting}
            disabled={!isFormValid()}
          >
            {t('referral.startCampaign') || 'Запустить кампанию'}
          </ButtonFilled>
        </div>
      </form>

      <Modal
        isOpen={!!errorModal}
        onRequestClose={() => setErrorModal(null)}
        style={errorModalStyles}
        ariaHideApp={false}
      >
        {errorModal && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '24px', color: '#D32F2F' }}>&#9888;</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#D32F2F' }}>
                {t('referral.configError') || 'Ошибка конфигурации'}
              </h3>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '15px', color: '#333', lineHeight: '1.5' }}>
              {errorModal}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonFilled
                variant="green"
                onClick={() => setErrorModal(null)}
              >
                {t('ok') || 'Понятно'}
              </ButtonFilled>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ReferralSystem